import React, {useEffect, useState, useRef, useCallback} from 'react';
import styled from 'styled-components';
import {Text} from '../../atoms/Text/Text';
import {Icon} from '../../atoms/Icon/Icon';
import {Button} from '../../atoms/Buttons/Button';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {ModalDialog} from '../../atoms/ModalDialog/ModalDialog';

type InfoType = 'error' | 'success' | 'refresh';

export interface InfoPopupProps {
  type: InfoType;
  headline: string;
  subline?: string;
  onClose?: () => void;
  onCtaClick?: () => void;
  ctaLabel?: string;
  showCloseIcon?: boolean;
  timeToAutoClose?: number;
  dataTestId?: string;
}

export const InfoPopup: React.FC<InfoPopupProps> = ({
  type,
  headline,
  subline,
  onClose,
  onCtaClick,
  ctaLabel,
  showCloseIcon = false,
  timeToAutoClose,
  dataTestId = 'infoPopup',
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const bodyRef = useRef<HTMLDivElement>(null);

  const closePopup = useCallback(() => {
    setIsOpen(false);

    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleButtonClick = () => {
    if (onCtaClick) {
      setIsOpen(false);
      onCtaClick();
    }
  };

  useEffect(() => {
    if (timeToAutoClose) {
      const timer = setTimeout(() => {
        if (bodyRef.current) {
          closePopup();
        }
      }, timeToAutoClose);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [timeToAutoClose, closePopup]);

  const iconAsset = getIconAsset(type);

  return (
    <ModalDialog
      contentFitWidth
      dataTestId={dataTestId}
      open={isOpen}
      onClose={closePopup}
      showCloseIcon={showCloseIcon}
      onModalClick={closePopup}
      bodyContent={
        <Container>
          {iconAsset && <Icon asset={iconAsset} size="M" />}
          <StyledText color="Black" size="px16" weight="bold">
            {headline}
          </StyledText>
          {subline && (
            <StyledText color="Grey1" size="px12" weight="mediumBold">
              {subline}
            </StyledText>
          )}
          {onCtaClick && <Cta onClick={handleButtonClick}>{ctaLabel}</Cta>}
        </Container>
      }
    />
  );
};

const getIconAsset = (type: InfoType) => {
  switch (type) {
    case 'error':
      return GenericIconName.AlertError;
    case 'success':
      return GenericIconName.AlertSuccess;
    case 'refresh':
      return GenericIconName.Reset;
    default:
      return undefined;
  }
};

const Container = styled.div`
  padding: 1.5rem 2rem 1.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const StyledText = styled(Text)`
  margin-top: 0.75rem;
  line-height: 1.5;
`;

const Cta = styled(Button)`
  margin-top: 26px;
`;
